import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Toaster } from 'react-hot-toast'

// Import pages
import DashboardPage from './pages/DashboardPage'
import ProjectsPage from './pages/ProjectsPage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import TeamsPage from './pages/TeamsPage'
import TeamDetailPage from './pages/TeamDetailPage'
import ProfilePage from './pages/ProfilePage'
import CommunityPage from './pages/CommunityPage'
import AnalyticsPage from './pages/AnalyticsPage'
import RevenuePage from './pages/RevenuePage'
import AuthPage from './pages/AuthPage'
import EditorPage from './pages/EditorPage'
import SupportPage from './pages/SupportPage'

// Import layout components
import Layout from './components/layout/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import CookieConsentBanner from './components/common/CookieConsent'
import MobileWarningModal from './components/common/MobileWarningModal'
import ReviewCompleteNotificationModal from './components/common/ReviewCompleteNotificationModal'

// For now, let's create a simple auth context
import { useState, useEffect, useRef, useCallback } from 'react'
import authService from './services/authService'
import studioNotificationsService, { type StudioNotification } from './services/studioNotificationsService'
import { useThemeStore } from './store/useThemeStore'
import { analyticsService } from './services/analyticsService'
import { useLocation } from 'react-router-dom'


//initialize theme on app start
const initializeTheme = () => {
  const { isDark } = useThemeStore.getState()
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

// Initialize theme immediately
initializeTheme()

// Inner component that has access to router hooks
function AppRouter() {
  // Auth state with loading
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false)
  const [user, setUser] = useState<any>(null)
  
  // Mobile warning modal state
  const [showMobileWarning, setShowMobileWarning] = useState(false)

  // Review complete notification queue
  const [pendingNotifications, setPendingNotifications] = useState<StudioNotification[]>([])
  const notificationPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Get location for page view tracking
  const location = useLocation()

  // Initialize analytics on app startup
  useEffect(() => {
    const initAnalytics = async () => {
      const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
      const enableAmplitude = import.meta.env.VITE_ENABLE_AMPLITUDE === 'true';
      const enableFirebase = import.meta.env.VITE_ENABLE_FIREBASE_ANALYTICS === 'true';
      
      await analyticsService.initialize({
        amplitudeApiKey,
        enableAmplitude,
        enableFirebase
      });
      
      // Set consent based on localStorage (CookieConsent will update this)
      const consent = localStorage.getItem('beemi_cookie_consent');
      if (consent === 'accepted') {
        analyticsService.setConsent(true);
      }
    };
    
    initAnalytics();
  }, []);

  useEffect(() => {
    // Only check authentication once on startup
    if (hasCheckedAuth) {
      console.log('🔄 Authentication already checked, skipping...');
      return;
    }

    // Test client logging system
    if (import.meta.env.DEV) {
      console.log('🎯 Client logging system initialized - this should appear in client-output.log');
    }

    // Check if user is already authenticated
    const checkAuth = async () => {
      console.log('🔍 Checking existing authentication on app startup...');
      setHasCheckedAuth(true);
      
      try {
        if (authService.isAuthenticated()) {
          console.log('🎫 Found existing tokens, verifying with server...');
          
          // Small delay to ensure localStorage is fully processed
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Fetch current user data
          try {
            const userData = await authService.getCurrentUser();
            console.log('👤 Current user data:', userData);
            const userObj = userData.user || userData;
            setUser(userObj); // Handle different response formats
            setIsAuthenticated(true);
            
            // Identify user in analytics
            if (userObj?.id) {
              analyticsService.identify(userObj.id, {
                user_id: userObj.id,
                username: userObj.username,
                display_name: userObj.display_name,
                email: userObj.primary_email,
                signup_date: userObj.created_at
              });
            }
          } catch (userError) {
            console.error('❌ Failed to fetch user data:', userError);
            
            // Check if this is a token-related error before clearing tokens
            const errorMessage = (userError as any)?.message || userError?.toString() || '';
            if (errorMessage.includes('refresh token') || errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
              console.log('🧹 Clearing invalid tokens due to auth error');
              authService.clearTokens();
            } else {
              console.log('⚠️ Non-auth error occurred, keeping tokens');
            }
            
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          console.log('❌ No existing tokens found');
          setIsAuthenticated(false)
          setUser(null)
        }
      } catch (error) {
        console.error('❌ Authentication check failed:', error)
        console.log('🧹 Clearing invalid tokens');
        // Clear invalid tokens
        authService.clearTokens()
        setIsAuthenticated(false)
      } finally {
        console.log('🔄 Authentication check complete');
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [hasCheckedAuth])

  // Check for mobile/small screen and show warning if not dismissed
  useEffect(() => {
    const checkMobileWarning = () => {
      // Check if user has already dismissed the warning
      const dismissed = localStorage.getItem('beemi_mobile_warning_dismissed');
      
      // Check if screen is mobile/small (less than 1024px - common tablet/laptop breakpoint)
      const isMobileOrSmall = window.innerWidth < 1024;
      
      // Show warning if: small screen AND not dismissed AND not loading
      if (isMobileOrSmall && !dismissed && !isLoading) {
        setShowMobileWarning(true);
      }
    };
    
    // Check on mount
    checkMobileWarning();
    
    // Check on window resize
    const handleResize = () => {
      checkMobileWarning();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLoading]);

  // Track page views on route changes
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const pageName = location.pathname.split('/')[1] || 'dashboard';
      analyticsService.track({
        name: 'page_viewed',
        properties: {
          page_name: pageName,
          path: location.pathname,
          search: location.search,
          referrer: document.referrer
        }
      });
    }
  }, [location, isLoading, isAuthenticated]);

  // Hide page loader with minimum display time
  useEffect(() => {
    const startTime = performance.now();
    const minDisplayTime = 0; // Minimum 800ms display time

    const hideLoader = () => {
      const loader = document.getElementById('page-loader');
      if (loader) {
        const elapsedTime = performance.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        setTimeout(() => {
          loader.classList.add('fade-out');
          // Remove from DOM after fade animation
          setTimeout(() => {
            loader.remove();
          }, 500); // Match the CSS transition duration
        }, remainingTime);
      }
    };

    // Wait for auth check to complete
    if (!isLoading) {
      hideLoader();
    }
  }, [isLoading]);

  // Poll for review complete notifications when authenticated
  const fetchNotifications = useCallback(async () => {
    try {
      const notifications = await studioNotificationsService.getUnread();
      if (notifications.length > 0) {
        setPendingNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const incoming = notifications.filter(n => !existingIds.has(n.id));
          return incoming.length > 0 ? [...prev, ...incoming] : prev;
        });
      }
    } catch {
      // Silently ignore — notification polling should never surface errors to the user
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (notificationPollRef.current) {
        clearInterval(notificationPollRef.current);
        notificationPollRef.current = null;
      }
      return;
    }

    // Fetch immediately on auth, then every 30 seconds
    fetchNotifications();
    notificationPollRef.current = setInterval(fetchNotifications, 30_000);

    return () => {
      if (notificationPollRef.current) {
        clearInterval(notificationPollRef.current);
        notificationPollRef.current = null;
      }
    };
  }, [isAuthenticated, fetchNotifications]);

  const handleDismissNotification = useCallback(async (notificationId: string) => {
    setPendingNotifications(prev => prev.filter(n => n.id !== notificationId));
    try {
      await studioNotificationsService.markRead(notificationId);
    } catch {
      // Best-effort — the notification is already removed from the UI
    }
  }, []);

  // Login function
  const login = async () => {
    console.log('🔐 User logged in successfully - setting authenticated state');
    try {
      // Fetch user data after login
      const userData = await authService.getCurrentUser();
      console.log('👤 User data after login:', userData);
      const userObj = userData.user || userData;
      setUser(userObj); // Handle different response formats
      setIsAuthenticated(true);
      
      // Identify user in analytics
      if (userObj?.id) {
        analyticsService.identify(userObj.id, {
          user_id: userObj.id,
          username: userObj.username,
          display_name: userObj.display_name,
          email: userObj.primary_email,
          signup_date: userObj.created_at
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch user data after login:', error);
      // Still mark as authenticated since login was successful
      setIsAuthenticated(true);
    }
    setIsLoading(false) // Ensure loading is complete
  }
  
  const logout = () => {
    console.log('🚪 User logging out - clearing tokens and state');
    authService.logout()
    setIsAuthenticated(false)
    setUser(null)
    setHasCheckedAuth(false) // Allow auth check on next mount
    
    // Reset analytics user data
    analyticsService.reset()
  }

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-text-muted">Loading...</p>
        </div>
      </div>
    )
  }

  // Debug logging for render
  console.log('🔄 AppRouter render - Auth State:', { isAuthenticated, isLoading, hasCheckedAuth });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <Routes>
        {/* Public route - redirect to dashboard if already authenticated */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? (
              (() => {
                console.log('🔀 Redirecting authenticated user from /login to dashboard');
                return <Navigate to="/" replace />
              })()
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <AuthPage onLogin={login} />
              </motion.div>
            )
          } 
        />
        
        {/* Editor route - full screen, no layout */}
        <Route 
          path="/editor/:projectId" 
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <EditorPage />
            </motion.div>
          } 
        />
        
        {/* Protected routes */}
        <Route 
          path="/*" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout onLogout={logout} user={user}>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                  <Route path="/teams" element={<TeamsPage />} />
                  <Route path="/teams/:teamId" element={<TeamDetailPage />} />
                  <Route path="/community" element={<CommunityPage />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  <Route path="/revenue" element={<RevenuePage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/support" element={<SupportPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } 
        />
      </Routes>
      
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg, #fff)',
            color: 'var(--toast-color, #333)',
            border: '1px solid var(--toast-border, #e5e7eb)',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      {/* Cookie Consent Banner */}
      <CookieConsentBanner />
      
      {/* Mobile Warning Modal */}
      <MobileWarningModal 
        isOpen={showMobileWarning}
        onClose={() => setShowMobileWarning(false)}
      />

      {/* Review Complete Notification Modal — rendered above all routes including editor */}
      <ReviewCompleteNotificationModal
        notification={pendingNotifications[0] ?? null}
        onDismiss={handleDismissNotification}
      />
    </div>
  )
}

function App() {
  return (
    <Router>
      <AppRouter />
    </Router>
  )
}

export default App 